import clsx from "clsx";
import { FC, Fragment, useMemo, useState } from "react";
import {
  Address,
  Asset,
  Balance,
  BalanceAccount,
  THOR,
  TxResult,
  gasToken,
  networkLabel,
  signers,
} from "rujira.js";
import {
  AssetOverviewModal,
  AssetOverviewOption,
  Button,
  IconDenom,
  NetworkIcon,
  Provider,
  TransferTimeValue,
  useTranslation,
} from "rujira.ui";
import { uniqueBalanceAccounts } from "../../services/balanceAccounts";
import { isSameCanonicalAsset } from "../../services/canonicalAsset";
import {
  compareDepositAssetPriority,
  isDirectRujiraDepositAsset,
} from "../../services/depositAssets";
import { DepositDetail, DepositDetailSubmitProps } from "./DepositDetail";
import { ReceivingInstructions } from "./ReceivingInstructions";
import {
  TransactionDetailRow,
  TransactionDetails,
  TransferAccountOption,
  TransferFooter,
  TransferModalNavigation,
} from "./TransferModal";
import { TransferSuccess } from "./TransferSuccess";

type ExchangeDepositStep = "assets" | "instructions" | "deposit";

export const ExchangeDeposit: FC<{
  assets: Asset[];
  balances: readonly Balance[];
  connectedAccounts: readonly {
    address: Address;
    provider: Provider.Key;
  }[];
  targets: Address[];
  target?: Address;
  Submit: FC<DepositDetailSubmitProps>;
  dismiss: () => void;
  back: () => void;
  connect: () => void;
  queryClient: signers.cosmos.QueryClient & signers.cosmos.ThorchainExtension;
  inboundAddresses?: { dustThreshold: bigint; chain: string }[];
}> = ({
  assets,
  balances,
  connectedAccounts,
  targets,
  target,
  Submit,
  dismiss,
  back,
  connect,
  queryClient,
  inboundAddresses,
}) => {
  const { t } = useTranslation("common");
  const [step, setStep] = useState<ExchangeDepositStep>("assets");
  const [asset, setAsset] = useState<Asset>();
  const [address, setAddress] = useState("");
  const [tx, setTx] = useState<TxResult>();
  const [instructionsComplete, setInstructionsComplete] = useState(false);
  const direct = Boolean(asset && isDirectRujiraDepositAsset(asset));
  const addressNetwork = direct ? THOR : asset?.chain;
  const addressOptions = useMemo(
    () =>
      addressNetwork
        ? connectedAddressOptions(connectedAccounts, addressNetwork)
        : [],
    [addressNetwork, connectedAccounts]
  );

  if (asset && instructionsComplete) {
    return (
      <TransferSuccess
        action="deposit"
        asset={asset}
        completionVariant="exchange"
        dismiss={dismiss}
      />
    );
  }

  if (asset && tx) {
    return (
      <TransferSuccess
        action="deposit"
        asset={asset}
        tx={tx}
        dismiss={dismiss}
      />
    );
  }

  if (step === "assets" || !asset) {
    const options: AssetOverviewOption[] = assets
      .map((item) => ({
        asset: item,
        networks: [item.chain],
        secondary: networkLabel(item.chain),
      }))
      .sort((left, right) =>
        compareDepositAssetPriority(left.asset, right.asset)
      );
    return (
      <AssetOverviewModal
        topBar={
          <TransferModalNavigation onBack={back}>
            <ExchangeDepositProgress step="assets" />
          </TransferModalNavigation>
        }
        title={t("exchangeDepositStartTitle")}
        description={t("exchangeDepositStartDescription")}
        searchPlaceholder={t("searchTokens")}
        options={options}
        emptyLabel={t("noBalancesFound")}
        onSelect={(selectedAsset) => {
          setAsset(selectedAsset);
          setAddress("");
          setStep("instructions");
        }}
      />
    );
  }

  if (step === "deposit") {
    return (
      <DepositDetail
        asset={asset}
        balance={sourceBalance(asset, address, balances)}
        targets={targets}
        target={
          targets.find((item) => item.address === target?.address) || targets[0]
        }
        Submit={Submit}
        onSuccess={setTx}
        cancel={dismiss}
        onBack={() => setStep("instructions")}
        connect={connect}
        queryClient={queryClient}
        inboundAddresses={inboundAddresses}
        progress={<ExchangeDepositProgress step="deposit" asset={asset} />}
        description={t("cexDepositHandoffDescription", {
          symbol: asset.metadata.symbol,
        })}
        showDestinationSuffix={false}
      />
    );
  }

  const chain = networkLabel(asset.chain);
  const feeToken = gasToken(asset.chain).symbol;
  const requiresGasToken = asset.metadata.symbol !== feeToken;
  const description = direct
    ? t("cexDirectDepositDescription", {
        symbol: asset.metadata.symbol,
      })
    : requiresGasToken
      ? t("cexDepositStepDescriptionWithGas", {
          symbol: asset.metadata.symbol,
          chain,
          gasToken: feeToken,
        })
      : t("cexDepositStepDescription", {
          symbol: asset.metadata.symbol,
          chain,
        });

  return (
    <div className="transfer-modal">
      <TransferModalNavigation
        onBack={() => {
          setAsset(undefined);
          setAddress("");
          setStep("assets");
        }}>
        <ExchangeDepositProgress step="instructions" asset={asset} />
      </TransferModalNavigation>
      <header className="transfer-modal__header">
        <h3 className="h3 mb-0">
          {t("moveAssetFromExchange", {
            symbol: asset.metadata.symbol,
          })}
        </h3>
        <p className="transfer-modal__description">{description}</p>
      </header>
      <ReceivingInstructions
        label={t("sendToThisAddress")}
        name="exchange-deposit-address"
        options={addressOptions}
        preferredAddress={target?.address}
        onAddressChange={setAddress}
        onConnect={connect}
        details={() => (
          <TransactionDetails title={t("details")}>
            <TransactionDetailRow label={t("asset")}>
              <span className="flex ai-c jc-e gap-0.5">
                <IconDenom
                  denom={asset.metadata.symbol}
                  className="block w-2 h-2"
                />
                {asset.metadata.symbol}
              </span>
            </TransactionDetailRow>
            <TransactionDetailRow label={t("network")}>
              <span className="flex ai-c jc-e gap-0.5">
                <NetworkIcon network={asset.chain} className="block w-2 h-2" />
                {chain}
              </span>
            </TransactionDetailRow>
            <TransactionDetailRow
              label={t("depositTime")}
              tooltip={t("cexDepositTimeTooltip")}>
              <TransferTimeValue network={asset.chain} />
            </TransactionDetailRow>
            <TransactionDetailRow label={t("networkFeeToken")}>
              <span className="flex ai-c jc-e gap-0.5">
                <IconDenom denom={feeToken} className="block w-2 h-2" />
                {feeToken}
              </span>
            </TransactionDetailRow>
          </TransactionDetails>
        )}
        footer={(selectedAddress) => (
          <TransferFooter
            cancel={dismiss}
            action={
              <Button
                label={direct ? t("done") : t("continueToDeposit")}
                disabled={!selectedAddress}
                onClick={() => {
                  setAddress(selectedAddress);
                  if (direct) setInstructionsComplete(true);
                  else setStep("deposit");
                }}
              />
            }
          />
        )}
      />
    </div>
  );
};

const ExchangeDepositProgress: FC<{
  step: ExchangeDepositStep;
  asset?: Asset;
}> = ({ step, asset }) => {
  const { t } = useTranslation("common");
  const direct = Boolean(asset && isDirectRujiraDepositAsset(asset));
  const activeIndex =
    step === "assets" ? 0 : direct || step === "instructions" ? 1 : 2;
  const steps = direct
    ? [t("exchange"), "Rujira"]
    : [
        t("exchange"),
        asset
          ? t("yourNetworkAddress", { network: networkLabel(asset.chain) })
          : t("yourAddress"),
        "Rujira",
      ];

  return (
    <nav
      className="flex ai-c wrap jc-e gap-0.5 fs-12 text-right"
      aria-label={t("cexDepositProgressLabel")}>
      {steps.map((label, index) => (
        <Fragment key={`${index}:${label}`}>
          {index > 0 && (
            <span className="color-grey" aria-hidden="true">
              &rarr;
            </span>
          )}
          <span
            className={clsx("fw-500", {
              "color-teal": index === activeIndex,
              "color-grey": index !== activeIndex,
            })}
            aria-current={index === activeIndex ? "step" : undefined}>
            {label}
          </span>
        </Fragment>
      ))}
    </nav>
  );
};

const connectedAddressOptions = (
  accounts: readonly {
    address: Address;
    provider: Provider.Key;
  }[],
  network: Asset["chain"]
): TransferAccountOption[] => {
  const byAddress = new Map<string, Provider.Key[]>();
  for (const item of accounts) {
    if (!item.address.networks.includes(network)) continue;
    const providers = byAddress.get(item.address.address) || [];
    if (!providers.includes(item.provider))
      byAddress.set(item.address.address, [...providers, item.provider]);
  }
  return [...byAddress.entries()].map(([address, providers]) => ({
    id: address,
    address,
    network,
    providers,
  }));
};

const sourceBalance = (
  asset: Asset,
  address: string,
  balances: readonly Balance[]
): Balance => {
  const accounts = uniqueBalanceAccounts(
    balances.flatMap((balance) => balance.accounts)
  ).filter(
    (account) =>
      account.address === address &&
      account.asset.chain === asset.chain &&
      isSameCanonicalAsset(account.asset.metadata.symbol, asset.metadata.symbol)
  );
  const sourceAccounts: readonly BalanceAccount[] = accounts.length
    ? accounts
    : address
      ? [{ address, asset, balance: 0n, valueUsd: 0n }]
      : [];

  return {
    asset,
    accounts: sourceAccounts,
    balance: sourceAccounts.reduce(
      (total, account) => total + account.balance,
      0n
    ),
    valueUsd: sourceAccounts.reduce(
      (total, account) => total + account.valueUsd,
      0n
    ),
  };
};
